/**
 * Component: Site navigation
 * ------------------------------------------------------------------------------
 * The meganav dropdown for vehicles.
 *
 * @namespace vehicleCarousel
 */
import log from 'salvo-lite/log'

import debounce from 'lodash-es/debounce'
import Flickity from 'flickity-imagesloaded'

import breakpoints from '../helpers/breakpoints'
import cssClasses from '../helpers/cssClasses'
import { on } from '../helpers/utils'

class VehicleCarousel {
    constructor (theme, elem) {
        this._theme = theme
        this._elem = elem
        /**
         * DOM this._selectors.
         */
        this._selectors = {
            sectionContainer: '[js-vehicle="container"]',
            navCarousel: '[js-nav-carousel="container"]',
            navCarouselCell: '[data-component-id="vehicle-nav-carousel"]',
            categoryTab: '[js-vehicle-types="tab"]',
            categoryTabState: '[js-vehicles-tab="state"]',
            categoryBlock: '[js-vehicle-types="block"]',
            carouselContainer: '[data-component-id*="vehicle-carousel"]',
            carouselCell: '[js-vehicle-carousel-cell="cell"]',
            carouselAttribute: 'data-component-id',
            tabAttribute: 'data-tab'
        }
        /**
         * DOM node selectors.
         */
        this._nodeSelectors = {
            sectionContainer: document.querySelector(this._selectors.sectionContainer),
            categoryTab: [...document.querySelectorAll(this._selectors.categoryTab)],
            categoryBlock: [...document.querySelectorAll(this._selectors.categoryBlock)],
            carouselContainer: [...document.querySelectorAll(this._selectors.carouselContainer)],
            carouselCell: [...document.querySelectorAll(this._selectors.carouselCell)],
            categoryTabState: document.querySelector(this._selectors.categoryTabState)
        }
        /**
         * Global variables
         */
        this._carouselContainer = []
        this._navCarousel = this._theme.getComponentsById('vehicle-nav-carousel')._carousel
        this._navCarouselComponent = this._theme.getComponentsById('vehicle-nav-carousel')
        log.debug(this.constructor.name, 'Constructed', this)
    }

    /**
     * Initialise component.
     */
    onInit () {
        if (!this._nodeSelectors.sectionContainer) {
            return
        }

        this._setDefaultState()
        this._setEventListeners()
    }

    _getAllCarousels () {
        this._nodeSelectors.carouselContainer.forEach((carousel, index) => {
            this._carouselContainer[index] = Flickity.data(this._theme.getComponentsById(`vehicle-carousel--${index}`)._elem)
        })
    }

    /**
     * Set default state based on screen size.
     */
    _setDefaultState () {
        this._getAllCarousels()
        this._handleResizeEvent()
        this._setActiveTabIndicator()
    }

    /**
     * Set event listeners.
     */
    _setEventListeners () {
        this._nodeSelectors.categoryTab.forEach((item, index) => {
            on('click', item, (event) => {
                const itemAttribute = item.getAttribute(this._selectors.tabAttribute)
                this._handleNavToggle(item, event, itemAttribute)
                this._removeActiveCells()
                this._carouselContainer[index].resize()

                this._nodeSelectors.categoryTabState.style.width = `${item.offsetWidth}px`
                this._nodeSelectors.categoryTabState.style.left = `${item.offsetLeft}px`
            })
        })

        on('resize', debounce(() => {
            this._handleResizeEvent()
            this._setActiveTabIndicator()
        }, 250))
    }

    /**
     * Handle resize events through debounce callback.
     */
    _handleResizeEvent () {
        if (!window.matchMedia(`(min-width: ${breakpoints.large})`).matches) {
            this._navCarouselComponent.onInit()
            this._navCarousel = this._theme.getComponentsById('vehicle-nav-carousel')._carousel
            this._nodeSelectors.carouselCell.forEach((item) => {
                on('click', item, (event) => {
                    this._handleModelBlockClick(event, item)
                    this._vehicleCarouselResize(item)
                })
            })

            return
        }

        this._navCarousel.destroy()

        this._nodeSelectors.carouselCell.forEach((item) => {
            on('mouseover', item, () => this._vehicleCarouselResize(item))
            on('mouseout', item, () => this._vehicleCarouselResize(item))
        })
    }

    /**
     * Handle vehicle carousel resize.
     * @param {Object} item the clicked element.
     */
    _vehicleCarouselResize (item) {
        const parentBlock = item.closest(this._selectors.carouselContainer)
        const carouselIndex = parentBlock.getAttribute(this._selectors.carouselAttribute).split('--')[1]
        this._carouselContainer[carouselIndex].resize()
    }

    /**
     * Vehicles dropdown active class toggling.
     * @param {Event} event the click event.
     * @param {Object} element the clicked element.
     */
    _handleModelBlockClick (event, element) {
        event.preventDefault()

        this._nodeSelectors.carouselCell.forEach((item) => {
            if (item === element) {
                item.classList.add(cssClasses.active)
                return
            }

            item.classList.remove(cssClasses.active)
        })
    }

    /**
     * Vehicle carousel nav toggle functions.
     * @param {Object} item the clicked element.
     * @param {Event} event the click event.
     * @param {String} attribute the attribute of the clicked element.
     */
    _handleNavToggle (item, event, attribute) {
        event.preventDefault()

        if (!item.classList.contains(cssClasses.active)) {
            this._removeActiveElements()
            item.classList.add(cssClasses.active)
        }

        this._nodeSelectors.categoryBlock.forEach((element) => {
            const elementAttribute = element.getAttribute(this._selectors.tabAttribute)

            if (elementAttribute === attribute) {
                element.classList.add(cssClasses.active)
            }
        })
    }

    /**
     * Remove active classes from all nav items and content blocks.
     */
    _removeActiveElements () {
        this._nodeSelectors.categoryTab.forEach((element) => {
            element.classList.remove(cssClasses.active)
        })

        this._nodeSelectors.categoryBlock.forEach((element) => {
            element.classList.remove(cssClasses.active)
        })
    }

    /**
     * Remove active classes from all carousel cells.
     */
    _removeActiveCells () {
        this._nodeSelectors.carouselCell.forEach((item) => {
            item.classList.remove(cssClasses.active)
        })
    }

    /**
     * Set position for active tab indicator.
     */
    _setActiveTabIndicator () {
        this._nodeSelectors.categoryTab.forEach((item) => {
            if (!item.classList.contains(cssClasses.active)) {
                return
            }

            this._nodeSelectors.categoryTabState.style.width = `${item.offsetWidth}px`
            this._nodeSelectors.categoryTabState.style.left = `${item.offsetLeft}px`
        })
    }

    // /**
    //  * Expose public interface.
    //  */
    // return Object.freeze({
    //   init,
    // })
}

export default VehicleCarousel



// WEBPACK FOOTER //
// ./src/resources/scripts/components/VehicleCarousel.js